import { lazy, Suspense } from "react";
import {
  Navigate,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from "react-router-dom";

/**
 * /pessoas/linha-do-tempo → ficha da pessoa. A tela virou o card Histórico da
 * ficha; o endereço fica para não quebrar favorito. `?pessoa=member:<id>` vai
 * para a ficha de membro, que redireciona sozinha quando é frequentador.
 * Visitante vai para /visitantes: a trajetória dele é o acompanhamento.
 */
function LinhaDoTempoLegacyRedirect() {
  const [searchParams] = useSearchParams();
  const [kind, id] = (searchParams.get("pessoa") || "").split(":");
  if (kind === "member" && id) {
    return <Navigate to={`/membros/visualizar/${id}`} replace />;
  }
  return <Navigate to={kind === "visitor" ? "/visitantes" : "/membros"} replace />;
}

/**
 * /bio/:slug → /:slug. `<Navigate to="/:slug">` não substitui o parâmetro:
 * mandava todo mundo para a página literal "/:slug", e todo link ou QR Code
 * antigo no formato /bio/... caía em "página não encontrada".
 */
function BioLegacyRedirect() {
  const { slug = "" } = useParams();
  return <Navigate to={`/${slug}`} replace />;
}
import { AnalyticsLoader } from "./components/analytics/analytics-loader";
import { CookieConsent } from "./components/analytics/cookie-consent";
import { DataFastLoader } from "./components/analytics/datafast-loader";
import { usePageTracking } from "./hooks/use-page-tracking";
// Rota principal nunca deve ser lazy — precisa renderizar imediatamente
import Landing from "./landing/page";
// Lazy load das páginas para code splitting
// ===============================
const AppProviders = lazy(() => import("./components/app-providers"));
const AdminRoute = lazy(() =>
  import("./components/admin-route").then((m) => ({ default: m.AdminRoute })),
);
const ProtectedRoute = lazy(() =>
  import("./components/protected-route").then((m) => ({
    default: m.ProtectedRoute,
  })),
);
const ProtectedWithPlanRoute = lazy(() =>
  import("./components/protected-with-plan-route").then((m) => ({
    default: m.ProtectedWithPlanRoute,
  })),
);
const PublicRoute = lazy(() =>
  import("./components/public-route").then((m) => ({ default: m.PublicRoute })),
);
const RegisterRoute = lazy(() =>
  import("./components/register-route").then((m) => ({
    default: m.RegisterRoute,
  })),
);
const PublicPlanGuard = lazy(() =>
  import("./components/public-plan-guard").then((m) => ({
    default: m.PublicPlanGuard,
  })),
);

// ===============================
// Landing / Marketing
// ===============================
const AboutPage = lazy(() => import("./landing/solo/about/page"));
const FeaturesPage = lazy(() => import("./landing/solo/features/page"));
const FeatureModulePage = lazy(
  () => import("./landing/solo/features/[slug]/page"),
);
const PricingPage = lazy(() => import("./landing/solo/pricing/page"));
// Peça de apoio à negociação, não conteúdo de busca: fica fora do sitemap e do
// STATIC_ROUTES de scripts/generate-og.mjs, e a própria página declara noindex.
const PropostaPage = lazy(() => import("./landing/solo/proposta/page"));
const FaqPage = lazy(() => import("./landing/solo/faq/page"));
const CarteirinhaPage = lazy(() => import("./landing/solo/carteirinha/page"));
// Onde o servidor imprime a carteirinha avulsa (functions/lib/card-order-pdf.js).
const CardOrderPrintPage = lazy(() => import("./app/carteirinha/impressao/page"));
const StatsPage = lazy(() => import("./landing/solo/stats/page"));
const PoliticaPrivacidadePage = lazy(
  () => import("./landing/solo/politica-privacidade/page"),
);
const PoliticaReembolsosPage = lazy(
  () => import("./landing/solo/politica-reembolsos/page"),
);
const TermosDeUsoPage = lazy(() => import("./landing/solo/termos-de-uso/page"));
const BlogListingPage = lazy(() => import("./landing/solo/blog/page"));
const BlogPostPage = lazy(() => import("./landing/solo/blog/[slug]/page"));
const BlogCategoryHubPage = lazy(
  () => import("./landing/solo/blog/categoria/[slug]/page"),
);
const ComparisonIndexPage = lazy(() => import("./landing/solo/comparar/page"));
const ComparisonPage = lazy(
  () => import("./landing/solo/comparar/[slug]/page"),
);
const ToolsIndexPage = lazy(() => import("./landing/solo/ferramentas/page"));
const ToolPage = lazy(() => import("./landing/solo/ferramentas/[slug]/page"));
const ComunidadePage = lazy(() => import("./landing/solo/comunidade/page"));
const DiagnosticoPage = lazy(() => import("./landing/solo/diagnostico/page"));

// ===============================
// Autenticação / App Base
// ===============================
const Login = lazy(() => import("./app/login/page"));
const Register = lazy(() => import("./app/register/page"));
const ResetPasswordPage = lazy(() => import("./app/reset-password/page"));
const CancelarExclusaoPage = lazy(
  () => import("./app/conta/cancelar-exclusao/page"),
);
const VerifyEmailPage = lazy(() => import("./app/verify-email/page"));
const Dashboard = lazy(() => import("./app/dashboard/page"));
const OnboardingPage = lazy(() => import("./app/onboarding/page"));

// ===============================
// Finanças
// ===============================
const FinanceLayout = lazy(() => import("./app/financas/layout"));
const FinanceDashboardPage = lazy(() => import("./app/financas/page"));
const FinanceAccountsPage = lazy(() => import("./app/financas/contas/page"));
const FinanceCategoriesPage = lazy(
  () => import("./app/financas/categorias/page"),
);
const FinanceEntriesPage = lazy(
  () => import("./app/financas/lancamentos/page"),
);
const FinanceCostCentersPage = lazy(
  () => import("./app/financas/centros-de-custo/page"),
);
const FinanceCardsPage = lazy(() => import("./app/financas/cartoes/page"));
const FinanceCardsDetailsPage = lazy(
  () => import("./app/financas/cartoes/[cardId]/page"),
);
const ExportacaoContabilPage = lazy(
  () => import("./app/financas/exportacao-contabil/page"),
);

// ===============================
// Pessoas - Dashboard
// ===============================
const PessoasDashboardPage = lazy(() => import("./app/pessoas/page"));

// ===============================
// Pessoas - Membros
// ===============================
const Membros = lazy(() => import("./app/pessoas/membros/page"));
const CadastroMembro = lazy(
  () => import("./app/pessoas/membros/cadastro/page"),
);
const EditarMembro = lazy(() => import("./app/pessoas/membros/editar/page"));
const VisualizarMembro = lazy(
  () => import("./app/pessoas/membros/visualizar/page"),
);
const ArvoreGenealogica = lazy(() => import("./app/pessoas/arvore/page"));
const MemberSelfRegistrationPage = lazy(
  () => import("./app/pessoas/membros/[idDaIgreja]/page"),
);

// ===============================
// Pessoas - Frequentadores
// ===============================
const FrequentadoresPage = lazy(
  () => import("./app/pessoas/frequentadores/page"),
);
const CadastroFrequentadorPage = lazy(
  () => import("./app/pessoas/frequentadores/cadastro/page"),
);
const EditarFrequentadorPage = lazy(
  () => import("./app/pessoas/frequentadores/editar/[id]/page"),
);
const VisualizarFrequentadorPage = lazy(
  () => import("./app/pessoas/frequentadores/visualizar/[id]/page"),
);

// ===============================
// Pessoas - Visitantes
// ===============================
const Visitantes = lazy(() => import("./app/pessoas/visitantes/page"));
const VisitorPublicPage = lazy(
  () => import("./app/pessoas/visitantes/[idDaIgreja]/page"),
);
const CentralDoMembroPage = lazy(
  () => import("./app/pessoas/central-do-membro/[idDaIgreja]/page"),
);

// ===============================
// Pessoas - Apelos
// ===============================
const ApeloPage = lazy(() => import("./app/pessoas/apelo/page"));
const ApeloPublicPage = lazy(
  () => import("./app/pessoas/apelo/[idDaIgreja]/page"),
);

// ===============================
// Pessoas - Pedidos de Oração
// ===============================
const PrayerRequestsPage = lazy(
  () => import("./app/pessoas/pedidos-oracao/page"),
);
const PrayerRequestsPublicPage = lazy(
  () => import("./app/pessoas/pedidos-oracao/[idDaIgreja]/page"),
);
const ConsolidacaoPage = lazy(() => import("./app/pessoas/consolidacao/page"));

// ===============================
// Pessoas - Grupos
// ===============================
const GruposPage = lazy(() => import("./app/pessoas/grupos/page"));
const GrupoDetalhesPage = lazy(() => import("./app/pessoas/grupos/[id]/page"));
const GruposDashboardPage = lazy(
  () => import("./app/pessoas/grupos/dashboard/page"),
);
const GruposConfiguracoesPage = lazy(
  () => import("./app/pessoas/grupos/configuracoes/page"),
);
const GruposOrganizacaoPage = lazy(
  () => import("./app/pessoas/grupos/organizacao/page"),
);
const GruposMapaPage = lazy(() => import("./app/pessoas/grupos/mapa/page"));
const GroupAttendancePublicPage = lazy(
  () => import("./app/pessoas/grupos/frequencia/[churchId]/page"),
);

// ===============================
// Pessoas - Batismos
// ===============================
const BatismosDashboardPage = lazy(() => import("./app/pessoas/batismos/page"));
const BaptismLegacyRedirects = () => import("./app/pessoas/batismos/legacy-redirects");
const BaptismQueueRedirect = lazy(() =>
  BaptismLegacyRedirects().then((m) => ({ default: m.BaptismQueueRedirect })),
);
const BaptismHomeRedirect = lazy(() =>
  BaptismLegacyRedirects().then((m) => ({ default: m.BaptismHomeRedirect })),
);
const BaptismEventRedirect = lazy(() =>
  BaptismLegacyRedirects().then((m) => ({ default: m.BaptismEventRedirect })),
);

// ===============================
// Igreja
// ===============================
const EditarIgreja = lazy(() => import("./app/igreja/editar/page"));

// ===============================
// Ministérios
// ===============================
const MinisteriosLayout = lazy(() => import("./app/ministerios/layout"));
const Ministerios = lazy(() => import("./app/ministerios/page"));
const JornadasPage = lazy(() => import("./app/ministerios/jornadas/page"));
const NovaJornadaPage = lazy(
  () => import("./app/ministerios/jornadas/nova/page"),
);
const JornadaDetalhesPage = lazy(
  () => import("./app/ministerios/jornadas/[id]/page"),
);
const JornadaFormPublicPage = lazy(
  () => import("./app/ministerios/jornadas/form/[idDaIgreja]/[journeyId]/page"),
);
const MinistriesDashboardPage = lazy(
  () => import("./app/ministerios/ministerios-dash/page"),
);
const MinistryLeaderDashboardPage = lazy(
  () => import("./app/ministerios/meu-dashboard/page"),
);
const LideradosPage = lazy(() => import("./app/ministerios/liderados/page"));
const CadastroMinisterio = lazy(
  () => import("./app/ministerios/cadastro/page"),
);
const EditarMinisterio = lazy(() => import("./app/ministerios/editar/page"));
const MinistryPositionsPage = lazy(
  () => import("./app/ministerios/[id]/posicoes/page"),
);
const RepertoirePage = lazy(
  () => import("./app/ministerios/[id]/repertorio/page"),
);
const MinistryHubLayout = lazy(() => import("./app/ministerios/[id]/layout"));
const MinistryHubIndex = lazy(() => import("./app/ministerios/[id]/page"));
const MinistryPeoplePage = lazy(
  () => import("./app/ministerios/[id]/pessoas/page"),
);
const MinistrySettingsPage = lazy(
  () => import("./app/ministerios/[id]/configuracoes/page"),
);

// ===============================
// Ministérios - Escalas
// ===============================
const EscalasPage = lazy(() => import("./app/ministerios/escalas/page"));
const MinistrySchedulePage = lazy(
  () => import("./app/ministerios/escalas/[ministryId]/page"),
);
const PublicSchedulePage = lazy(
  () => import("./app/ministerios/escalas/publico/[slug]/page"),
);
const PublicAttendancePage = lazy(
  () => import("./app/ministerios/escalas/presenca/[slug]/page"),
);
const GeneralMonthlySchedulePage = lazy(
  () => import("./app/ministerios/geral/page"),
);
const PublicGeneralSchedulePage = lazy(
  () => import("./app/ministerios/escalas/publico/geral/page"),
);
const AvailabilityPage = lazy(
  () =>
    import("./app/ministerios/escalas/disponibilidade/[ministryId]/[month]/[year]/page"),
);
const DayPreferencePage = lazy(
  () => import("./app/ministerios/escalas/preferencias/[ministryId]/page"),
);

// ===============================
// Ministérios - Chamada
// ===============================
const RollCallPage = lazy(() => import("./app/ministerios/chamada/page"));
const RollCallPublicPage = lazy(
  () => import("./app/ministerios/chamada/[idDaIgreja]/page"),
);

// ===============================
// Atividades - Eventos
// ===============================
const EventsPage = lazy(() => import("./app/atividades/events/page"));
const EventsPublicPage = lazy(
  () => import("./app/atividades/events/[idDaIgreja]/page"),
);
const EventPublicDetailPage = lazy(
  () => import("./app/atividades/events/[idDaIgreja]/[eventId]/page"),
);
const PublicEventMetricsPage = lazy(
  () => import("./app/atividades/events/metricas/[token]/page"),
);
const EventRegistrationManagementPage = lazy(
  () =>
    import("./app/atividades/events/[idDaIgreja]/[eventId]/gerenciar/[registrationId]/page"),
);
const EventConfirmationPage = lazy(
  () => import("./app/atividades/events/confirmacao/page"),
);
const EventRegistrationsPage = lazy(
  () => import("./app/atividades/events/inscricoes/[eventId]/page"),
);
const EventFormBuilderPage = lazy(
  () => import("./app/atividades/events/formulario/[eventId]/page"),
);
const EventSettingsPage = lazy(
  () => import("./app/atividades/events/configuracoes/[eventId]/page"),
);
const EventAnalyticsPage = lazy(
  () => import("./app/atividades/events/analytics/[eventId]/page"),
);
const EventAttendancePage = lazy(
  () => import("./app/atividades/events/frequencia/[eventId]/page"),
);

// ===============================
// Atividades - Cronograma
// ===============================
const CronogramaPage = lazy(() => import("./app/atividades/cronograma/page"));
const NovoCronogramaPage = lazy(
  () => import("./app/atividades/cronograma/novo/page"),
);
const EditarCronogramaPage = lazy(
  () => import("./app/atividades/cronograma/editar/[id]/page"),
);
const CultosRecorrentesPage = lazy(
  () => import("./app/atividades/cronograma/cultos-recorrentes/page"),
);
const CronogramaPublicoPage = lazy(
  () => import("./app/atividades/cronograma/[id]/publico/page"),
);

// ===============================
// Atividades - Cultos / Contagem
// ===============================
const ServiceCountsPage = lazy(() => import("./app/atividades/contagem/page"));
const PublicServiceCountPage = lazy(
  () => import("./app/atividades/contagem/[idDaIgreja]/page"),
);
const AttendanceQrPage = lazy(
  () => import("./app/atividades/contagem/presenca/[idDaIgreja]/page"),
);

// ===============================
// Atividades - Evangelismo
const EvangelismListPage = lazy(
  () => import("./app/atividades/evangelismo/page"),
);
const EvangelismCampaignPage = lazy(
  () => import("./app/atividades/evangelismo/[campaignId]/page"),
);
const EvangelismPublicPage = lazy(
  () =>
    import("./app/atividades/evangelismo/publico/[idDaIgreja]/[campaignId]/page"),
);

// Atividades - Votações
// ===============================
const PollsListPage = lazy(() => import("./app/atividades/votacoes/page"));
const PollDetailsPage = lazy(
  () => import("./app/atividades/votacoes/[pollId]/page"),
);
const PublicVotePage = lazy(() => import("./app/public/vote/[pollId]/page"));

// ===============================
// Atividades
// ===============================
const AtividadesLayout = lazy(() => import("./app/atividades/layout"));
const AtividadesPage = lazy(() => import("./app/atividades/page"));

// ===============================
// Atividades - Agenda
// ===============================
const AgendaPage = lazy(() => import("./app/atividades/agenda/page"));
const AgendaPublicPage = lazy(
  () => import("./app/atividades/agenda/[idDaIgreja]/page"),
);
const AgendaBookingStatusPage = lazy(
  () => import("./app/atividades/agenda/[idDaIgreja]/reserva/[bookingId]/page"),
);

// ===============================
// Atividades - Corrente de Oração
// ===============================
const PrayerChainsPage = lazy(() => import("./app/atividades/oracao/page"));
const PrayerChainPanelPage = lazy(
  () => import("./app/atividades/oracao/[chainId]/page"),
);
const PrayerChainSettingsPage = lazy(
  () => import("./app/atividades/oracao/configuracoes/[chainId]/page"),
);
const PrayerChainPublicPage = lazy(
  () => import("./app/atividades/oracao/publico/[idDaIgreja]/[chainId]/page"),
);

// ===============================
// Kids
// ===============================
const KidsLayout = lazy(() => import("./app/kids/layout"));
const KidsPage = lazy(() => import("./app/kids/page"));
const FaixasEtariasPage = lazy(() => import("./app/kids/faixas-etarias/page"));
const CriancasPage = lazy(() => import("./app/kids/criancas/page"));
const CadastroCriancaPage = lazy(
  () => import("./app/kids/criancas/cadastro/page"),
);
const VisualizarCriancaPage = lazy(
  () => import("./app/kids/criancas/visualizar/[id]/page"),
);
const EditarCriancaPage = lazy(
  () => import("./app/kids/criancas/editar/[id]/page"),
);
const ChildPublicRegistrationPage = lazy(
  () => import("./app/kids/criancas/[idDaIgreja]/page"),
);
const CheckInPage = lazy(() => import("./app/kids/check-in/page"));
const SalaPage = lazy(() => import("./app/kids/sala/[id]/page"));
const FrequenciaPage = lazy(() => import("./app/kids/frequencia/page"));
const KidsPrintStationPage = lazy(() => import("./app/kids/impressao/page"));
const KidsCheckInScreenPage = lazy(
  () => import("./app/kids/tela-checkin/page"),
);

// ===============================
// Relatórios
// ===============================
const Relatorios = lazy(() => import("./app/relatorios/page"));
const MembrosPorMinisterio = lazy(
  () => import("./app/relatorios/membros-por-ministerio/page"),
);
const CrescimentoMembros = lazy(
  () => import("./app/relatorios/crescimento-membros/page"),
);
const DemografiaMembros = lazy(
  () => import("./app/relatorios/demografia-membros/page"),
);
const QualidadeCadastro = lazy(
  () => import("./app/relatorios/qualidade-cadastro/page"),
);
const FrequentadoresReportPage = lazy(
  () => import("./app/relatorios/frequentadores/page"),
);
const AtividadeMembros = lazy(
  () => import("./app/relatorios/atividade-membros/page"),
);
const CrescimentoMinisterios = lazy(
  () => import("./app/relatorios/crescimento-ministerios/page"),
);
const Aniversariantes = lazy(
  () => import("./app/relatorios/aniversariantes/page"),
);
const VisitantesReportPage = lazy(
  () => import("./app/relatorios/visitantes/page"),
);
const VisitantesConvertidosReportPage = lazy(
  () => import("./app/relatorios/visitantes-convertidos/page"),
);
const ApelosReportPage = lazy(() => import("./app/relatorios/apelos/page"));
const KidsPickupReportPage = lazy(
  () => import("./app/relatorios/infantil-retirada/page"),
);
const KidsRoomsReportPage = lazy(
  () => import("./app/relatorios/infantil-salas/page"),
);
const KidsVisitorsReportPage = lazy(
  () => import("./app/relatorios/infantil-visitantes/page"),
);
const KidsRecordsReportPage = lazy(
  () => import("./app/relatorios/infantil-fichas/page"),
);
const Chamadas = lazy(() => import("./app/relatorios/chamada/page"));
const FinanceReportsPage = lazy(
  () => import("./app/relatorios/financeiros/page"),
);
const BatismosReportPage = lazy(() => import("./app/relatorios/batismos/page"));
const BatizadosNaIgrejaPage = lazy(
  () => import("./app/relatorios/batizados-na-igreja/page"),
);
const CamposPersonalizadosReportPage = lazy(
  () => import("./app/relatorios/campos-personalizados/page"),
);
const EsfriamentoReportPage = lazy(
  () => import("./app/relatorios/esfriamento/page"),
);
const ComparativoCrescimentoPage = lazy(
  () => import("./app/relatorios/comparativo-crescimento/page"),
);
const ServiceReportsPage = lazy(() => import("./app/relatorios/cultos/page"));
const GruposRelatoriosPage = lazy(() => import("./app/relatorios/grupos/page"));
const PresencaEscalasReportPage = lazy(
  () => import("./app/relatorios/presenca-escalas/page"),
);
const MemberScheduleReportPage = lazy(
  () => import("./app/relatorios/presenca-escalas/[memberId]/page"),
);
const CargaEscalaReportPage = lazy(
  () => import("./app/relatorios/carga-escala/page"),
);
const CoberturaPosicoesReportPage = lazy(
  () => import("./app/relatorios/cobertura-posicoes/page"),
);
const DisponibilidadeReportPage = lazy(
  () => import("./app/relatorios/disponibilidade/page"),
);
const FluxoDeCaixaReportPage = lazy(
  () => import("./app/relatorios/fluxo-de-caixa/page"),
);
const ContribuintesReportPage = lazy(
  () => import("./app/relatorios/contribuintes/page"),
);
const ContasAPagarReportPage = lazy(
  () => import("./app/relatorios/contas-a-pagar/page"),
);

// ===============================
// Configurações
// ===============================
const ConfiguracoesIgreja = lazy(
  () => import("./app/configuracoes/igreja/page"),
);
const ConfiguracoesUsuarios = lazy(
  () => import("./app/configuracoes/usuarios/page"),
);
const ConfiguracoesAssinatura = lazy(
  () => import("./app/configuracoes/assinatura/page"),
);
const ConfiguracoesAfiliados = lazy(
  () => import("./app/configuracoes/afiliados/page"),
);
const ConfiguracoesPagamentos = lazy(
  () => import("./app/configuracoes/pagamentos/page"),
);

const LogsPage = lazy(() => import("./app/auditoria/page"));

// ===============================
// Bio / Links Públicos
// ===============================
const BioLinksPage = lazy(() => import("./app/bio/bio-links/page"));
const BioPublicPage = lazy(() => import("./app/bio/[slug]/page"));

// ===============================
// Obrigado / Feedback
// ===============================
const ObrigadoPage = lazy(() => import("./app/obrigado/page"));
const ChurnFeedbackPage = lazy(() => import("./app/feedback/churn/page"));
const GenericFeedbackPage = lazy(() => import("./app/feedback/[formId]/page"));
const AvaliarPage = lazy(() => import("./app/avaliar/page"));

// ===============================
// Admin
// ===============================
const AdminLoginPage = lazy(() => import("./app/admin/login/page"));
const AdminLayout = lazy(() => import("./app/admin/layout"));
const NotFoundPage = lazy(() => import("./app/not-found"));
const AdminDashboardPage = lazy(() => import("./app/admin/dashboard/page"));

// ===============================
// Cantina
// ===============================
const CantinaLayout = lazy(() => import("./app/cantina/layout"));
const CantinaPage = lazy(() => import("./app/cantina/page"));
const CantinaPOSPage = lazy(() => import("./app/cantina/venda/page"));
const CantinaMenuPage = lazy(() => import("./app/cantina/cardapio/page"));
const CantinaTabsPage = lazy(() => import("./app/cantina/fiados/page"));
const RelatoriosPage = lazy(() => import("./app/cantina/relatorios/page"));
const LojaLayout = lazy(() => import("./app/loja/layout"));
const LojaPage = lazy(() => import("./app/loja/page"));
const CantinaGastosPage = lazy(() => import("./app/cantina/gastos/page"));
const CantinaFaturamentoPage = lazy(
  () => import("./app/cantina/faturamento/page"),
);

// ===============================
// Projetos / Tarefas
// ===============================
const ProjetosLayout = lazy(() => import("./app/projetos/layout"));
const ProjetosDashboardPage = lazy(() => import("./app/projetos/page"));
const ProjetoKanbanPage = lazy(() => import("./app/projetos/[id]/page"));

// ===============================
// Treinamentos
// ===============================
const TreinamentosPage = lazy(() => import("./app/treinamentos/page"));
const TreinamentoDetalhesPage = lazy(
  () => import("./app/treinamentos/[id]/page"),
);
const TreinamentoPublicoPage = lazy(
  () => import("./app/treinamentos/publico/[idDaIgreja]/[trainingId]/page"),
);

// ===============================
// Dev
// ===============================
// Rota que estoura de propósito para inspecionar o RootErrorBoundary em tela.
// `import.meta.env.DEV` vira `false` literal no build, então o import dinâmico
// é removido pelo Rollup e o módulo nunca vai para produção.
const DevErrorTrigger = import.meta.env.DEV
  ? lazy(() => import("./components/dev-error-trigger"))
  : null;

export default function MainRoutes() {
  usePageTracking();

  return (
    <>
      <AnalyticsLoader />
      <DataFastLoader />
      <CookieConsent />
      <Suspense
        fallback={
          <div className="relative min-h-screen w-full bg-background">
            <div className="loader">
              <div className="ecclesiato-loader" />
            </div>
          </div>
        }
      >
        <div className="flex flex-col min-h-screen w-full">
          <Routes>
            {/* Só em dev: /__erro e /__erro?tipo=chunk mostram o error boundary */}
            {DevErrorTrigger && (
              <Route path="/__erro" element={<DevErrorTrigger />} />
            )}

            {/* Public route - accessible to all */}
            <Route path="/" element={<Landing />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/features/:slug" element={<FeatureModulePage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/proposta" element={<PropostaPage />} />
            <Route path="/carteirinha" element={<CarteirinhaPage />} />
            <Route
              path="/carteirinha/impressao"
              element={<CardOrderPrintPage />}
            />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/estatisticas" element={<StatsPage />} />
            <Route
              path="/politica-privacidade"
              element={<PoliticaPrivacidadePage />}
            />
            <Route
              path="/politica-reembolsos"
              element={<PoliticaReembolsosPage />}
            />
            <Route path="/termos-de-uso" element={<TermosDeUsoPage />} />
            <Route path="/blog" element={<BlogListingPage />} />
            <Route
              path="/blog/categoria/:slug"
              element={<BlogCategoryHubPage />}
            />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/comparar" element={<ComparisonIndexPage />} />
            <Route path="/comparar/:slug" element={<ComparisonPage />} />
            <Route path="/ferramentas" element={<ToolsIndexPage />} />
            <Route path="/ferramentas/:slug" element={<ToolPage />} />
            <Route path="/comunidade" element={<ComunidadePage />} />
            <Route path="/diagnostico" element={<DiagnosticoPage />} />
            <Route path="/feedback/churn" element={<ChurnFeedbackPage />} />
            <Route path="/feedback/:formId" element={<GenericFeedbackPage />} />
            {/* Avaliação da landing. Fora do AppProviders e sem
                ProtectedRoute: a página resolve auth sozinha e devolve o
                deslogado para cá depois do login. Ver app/avaliar/page.tsx. */}
            <Route path="/avaliar" element={<AvaliarPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            {/* Cancelamento da exclusão de conta pelo link do e-mail. Fora de
                qualquer guarda de autenticação de propósito: se o pedido de
                exclusão partiu de um invasor, ele pode ter trocado a senha, e
                exigir login aqui fecharia a única saída do titular. */}
            <Route
              path="/conta/cancelar-exclusao"
              element={<CancelarExclusaoPage />}
            />

            {/* ============================================================
                Rotas públicas (fora do AppProviders): páginas acessadas por
                visitantes anônimos via link direto. Não usam os contexts de
                igreja/ministério/membro/etc, então ficam fora do AppProviders
                para não pagar o custo de montar 12 providers + sidebar +
                banners, nem inicializar o Firebase Auth à toa.
               ============================================================ */}

            {/* Igrejas no plano gratuito não têm links públicos: o
                PublicPlanGuard resolve a igreja pela própria URL e bloqueia a
                página quando o plano é "free". */}
            <Route element={<PublicPlanGuard resolver="params" />}>
              {/* Escalas - públicas */}
              <Route
                path="/escalas/publico/geral/:churchId"
                element={<PublicGeneralSchedulePage />}
              />
              <Route
                path="/escalas/disponibilidade/:churchId/:ministryId/:month/:year"
                element={<AvailabilityPage />}
              />
              <Route
                path="/escalas/preferencias/:churchId/:ministryId"
                element={<DayPreferencePage />}
              />

              {/* Jornadas - pública */}
              <Route
                path="/ministerios/jornadas/form/:idDaIgreja/:journeyId"
                element={<JornadaFormPublicPage />}
              />

              {/* Chamada - pública */}
              <Route
                path="/ministerios/chamada/:idDaIgreja"
                element={<RollCallPublicPage />}
              />
              <Route
                path="/ministerios/chamada/:idDaIgreja/:idDaChamada"
                element={<RollCallPublicPage />}
              />
              <Route
                path="/chamada/:idDaIgreja"
                element={<RollCallPublicPage />}
              />
              <Route
                path="/chamada/:idDaIgreja/:idDaChamada"
                element={<RollCallPublicPage />}
              />

              {/* Pedidos de Oração - pública */}
              <Route
                path="/pessoas/pedidos-oracao/:idDaIgreja"
                element={<PrayerRequestsPublicPage />}
              />

              {/* Membros - pública (auto-cadastro) */}
              <Route
                path="/membros/:idDaIgreja"
                element={<MemberSelfRegistrationPage />}
              />
              <Route
                path="/pessoas/membros/:idDaIgreja"
                element={<MemberSelfRegistrationPage />}
              />
              <Route
                path="/centraldomembro/:idDaIgreja"
                element={<CentralDoMembroPage />}
              />

              {/* Visitantes - pública */}
              <Route
                path="/visitantes/:idDaIgreja"
                element={<VisitorPublicPage />}
              />

              {/* Eventos - pública */}
              {/* Antes das rotas por igreja: "metricas" é segmento fixo e não
                  pode cair no curinga /events/:idDaIgreja/:eventId. */}
              <Route
                path="/events/metricas/:token"
                element={<PublicEventMetricsPage />}
              />
              <Route
                path="/events/:idDaIgreja"
                element={<EventsPublicPage />}
              />
              <Route
                path="/events/:idDaIgreja/:eventId"
                element={<EventPublicDetailPage />}
              />
              <Route
                path="/events/:idDaIgreja/:eventId/gerenciar/:registrationId"
                element={<EventRegistrationManagementPage />}
              />

              {/* Apelo - pública */}
              <Route path="/apelo/:idDaIgreja" element={<ApeloPublicPage />} />

              {/* Contagem Culto - pública */}
              <Route
                path="/cultos/contagem/:idDaIgreja"
                element={<PublicServiceCountPage />}
              />

              {/* Presença por QR - pública */}
              <Route
                path="/cultos/presenca/:idDaIgreja"
                element={<AttendanceQrPage />}
              />

              {/* Grupos - Frequência Pública */}
              <Route
                path="/grupos/frequencia/:churchId"
                element={<GroupAttendancePublicPage />}
              />

              {/* Evangelismo - pública (inscrição do membro e o grupo dele) */}
              <Route
                path="/evangelismo/:idDaIgreja/:campaignId"
                element={<EvangelismPublicPage />}
              />

              {/* Corrente de Oração - pública (relógio de oração / vigília) */}
              <Route
                path="/oracao/:idDaIgreja/:chainId"
                element={<PrayerChainPublicPage />}
              />

              {/* Agenda - pública */}
              <Route
                path="/agenda/:idDaIgreja"
                element={<AgendaPublicPage />}
              />

              {/* Agenda - acompanhamento de reserva de espaço */}
              <Route
                path="/agenda/:idDaIgreja/reserva/:bookingId"
                element={<AgendaBookingStatusPage />}
              />

              {/* Treinamentos - pública (trilha do aluno) */}
              <Route
                path="/treinamentos/publico/:idDaIgreja/:trainingId"
                element={<TreinamentoPublicoPage />}
              />

              {/* Crianças - pública (auto-cadastro) */}
              <Route
                path="/kids/criancas/:idDaIgreja"
                element={<ChildPublicRegistrationPage />}
              />
            </Route>

            {/* Escalas - públicas por slug */}
            <Route element={<PublicPlanGuard resolver="scheduleSlug" />}>
              <Route path="/escalas/v/:slug" element={<PublicSchedulePage />} />
              <Route
                path="/escalas/presenca/:slug"
                element={<PublicAttendancePage />}
              />
            </Route>

            {/* Cronograma - pública */}
            <Route element={<PublicPlanGuard resolver="cronogramaSlug" />}>
              <Route
                path="/cronograma/:slug"
                element={<CronogramaPublicoPage />}
              />
            </Route>

            {/* Votações - pública (igreja vem da query `?cid=`) */}
            <Route element={<PublicPlanGuard resolver="pollQuery" />}>
              <Route path="/public/vote/:pollId" element={<PublicVotePage />} />
            </Route>

            {/* Retrocompatibilidade: links antigos sem churchId na URL. Sem o id
                da igreja na URL não há como validar o plano antes de renderizar
                — a própria página descobre a igreja pelo ministério. */}
            <Route
              path="/escalas/disponibilidade/:ministryId/:month/:year"
              element={<AvailabilityPage />}
            />
            <Route
              path="/escalas/preferencias/:ministryId"
              element={<DayPreferencePage />}
            />

            {/* Confirmação de inscrição - não carrega dados da igreja */}
            <Route
              path="/events/inscricao/confirmacao"
              element={<EventConfirmationPage />}
            />

            {/* Bio Links - pública (redirecionamento para o novo formato) */}
            <Route
              path="/bio/:slug"
              element={<BioLegacyRedirect />}
            />

            <Route element={<AppProviders />}>
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLoginPage />} />

              <Route element={<AdminRoute />}>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route path="dashboard" element={<AdminDashboardPage />} />
                  {/* Add more nested admin routes here */}
                </Route>
              </Route>

              {/* Protected routes - only accessible when NOT authenticated */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              {/* RegisterRoute, e não PublicRoute: quem já tem conta mas
                  ainda não tem igreja precisa desta tela para terminar o
                  cadastro. Ver components/register-route.tsx. */}
              <Route
                path="/register"
                element={
                  <RegisterRoute>
                    <Register />
                  </RegisterRoute>
                }
              />

              {/* Protected route - only accessible when authenticated and with active plan */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedWithPlanRoute>
                    <Dashboard />
                  </ProtectedWithPlanRoute>
                }
              />

              {/* Configuração inicial. Ficava no ProtectedRoute para não
                  barrar quem acabou de assinar e chega antes de a
                  sincronização do plano terminar — só que sem plano nenhum
                  quer dizer sem guard nenhum, e a conta que cadastrou a igreja
                  e desistiu do checkout entrava aqui digitando a URL. O
                  dashboard já escondia a barra de primeiros passos nesse caso
                  ("na demonstração ela seria uma lista de portas trancadas"),
                  e a porta continuava destrancada.

                  A janela da sincronização passou a ser tratada dentro do
                  próprio guard, por `premiumStatus.isPremium`. */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedWithPlanRoute>
                    <OnboardingPage />
                  </ProtectedWithPlanRoute>
                }
              />

              {/* Finanças routes */}
              <Route
                path="/financas"
                element={
                  <ProtectedWithPlanRoute>
                    <FinanceLayout />
                  </ProtectedWithPlanRoute>
                }
              >
                <Route index element={<FinanceDashboardPage />} />
                <Route path="contas" element={<FinanceAccountsPage />} />
                <Route path="categorias" element={<FinanceCategoriesPage />} />
                <Route path="lancamentos" element={<FinanceEntriesPage />} />
                <Route
                  path="centros-de-custo"
                  element={<FinanceCostCentersPage />}
                />
                <Route path="cartoes" element={<FinanceCardsPage />} />
                <Route
                  path="cartoes/:cardId"
                  element={<FinanceCardsDetailsPage />}
                />
                <Route
                  path="exportacao-contabil"
                  element={<ExportacaoContabilPage />}
                />
              </Route>

              <Route
                path="/pessoas"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/pessoas"]}
                  >
                    <PessoasDashboardPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/pessoas/consolidacao"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/pessoas"]}
                  >
                    <ConsolidacaoPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/pessoas/pedidos-oracao"
                element={
                  // Só dono e admin: pedido de oração é dado sensível (a
                  // regra do Firestore também só deixa os dois lerem)
                  <ProtectedWithPlanRoute>
                    <PrayerRequestsPage />
                  </ProtectedWithPlanRoute>
                }
              />
              {/* Membros routes */}
              <Route
                path="/membros"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/membros"]}
                  >
                    <Membros />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/membros/cadastro"
                element={
                  <ProtectedWithPlanRoute>
                    <CadastroMembro />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/membros/visualizar/:id"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/membros"]}
                  >
                    <VisualizarMembro />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/membros/arvore"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/membros"]}
                  >
                    <ArvoreGenealogica />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/membros/editar/:id"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/membros"]}
                  >
                    <EditarMembro />
                  </ProtectedWithPlanRoute>
                }
              />
              {/* Frequentadores routes */}
              <Route
                path="/frequentadores"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/frequentadores"]}
                  >
                    <FrequentadoresPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/frequentadores/cadastro"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/frequentadores"]}
                  >
                    <CadastroFrequentadorPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/frequentadores/visualizar/:id"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/frequentadores"]}
                  >
                    <VisualizarFrequentadorPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/frequentadores/editar/:id"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/frequentadores"]}
                  >
                    <EditarFrequentadorPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/igreja/editar/:id"
                element={
                  <ProtectedWithPlanRoute>
                    <EditarIgreja />
                  </ProtectedWithPlanRoute>
                }
              />

              {/* Ministérios routes */}
              <Route
                path="/ministerios"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={[
                      "/ministerios",
                      "/ministerios/lista",
                      "/ministerios/escalas",
                    ]}
                  >
                    <MinisteriosLayout />
                  </ProtectedWithPlanRoute>
                }
              >
                {/* Dashboard (Index) */}
                <Route
                  index
                  element={
                    <ProtectedWithPlanRoute>
                      <MinistriesDashboardPage />
                    </ProtectedWithPlanRoute>
                  }
                />

                {/* Meu Dashboard (Líder) */}
                <Route
                  path="meu-dashboard"
                  element={
                    <ProtectedWithPlanRoute
                      allowedForMinistryLeader={["/ministerios"]}
                    >
                      <MinistryLeaderDashboardPage />
                    </ProtectedWithPlanRoute>
                  }
                />

                {/* Liderados: nota e ranking (líder e admin) */}
                <Route
                  path="liderados"
                  element={
                    <ProtectedWithPlanRoute
                      allowedForMinistryLeader={["/ministerios/liderados"]}
                    >
                      <LideradosPage />
                    </ProtectedWithPlanRoute>
                  }
                />

                {/* Lista de Ministérios (Antigo /ministerios) */}
                <Route
                  path="lista"
                  element={
                    <ProtectedWithPlanRoute
                      allowedForMinistryLeader={["/ministerios"]}
                    >
                      <Ministerios />
                    </ProtectedWithPlanRoute>
                  }
                />

                {/* Cadastro */}
                <Route
                  path="cadastro"
                  element={
                    <ProtectedWithPlanRoute>
                      <CadastroMinisterio />
                    </ProtectedWithPlanRoute>
                  }
                />

                {/* Editar: virou a aba Configurações; a rota só redireciona. */}
                <Route
                  path="editar/:id"
                  element={
                    <ProtectedWithPlanRoute>
                      <EditarMinisterio />
                    </ProtectedWithPlanRoute>
                  }
                />

                {/* Página do ministério: pessoas, posições, repertório e
                    configurações como abas. As rotas estáticas acima (lista,
                    cadastro, escalas…) têm prioridade sobre o :id. */}
                <Route
                  path=":id"
                  element={
                    <ProtectedWithPlanRoute
                      allowedForMinistryLeader={["/ministerios"]}
                    >
                      <MinistryHubLayout />
                    </ProtectedWithPlanRoute>
                  }
                >
                  <Route index element={<MinistryHubIndex />} />
                  <Route path="pessoas" element={<MinistryPeoplePage />} />
                  <Route path="posicoes" element={<MinistryPositionsPage />} />
                  <Route path="repertorio" element={<RepertoirePage />} />
                  {/* Sem allowedForMinistryLeader: é do administrador. */}
                  <Route
                    path="configuracoes"
                    element={
                      <ProtectedWithPlanRoute>
                        <MinistrySettingsPage />
                      </ProtectedWithPlanRoute>
                    }
                  />
                </Route>

                {/* Escalas Sub-module */}
                <Route path="escalas">
                  <Route
                    index
                    element={
                      <ProtectedWithPlanRoute
                        allowedForMinistryLeader={["/ministerios/escalas"]}
                      >
                        <EscalasPage />
                      </ProtectedWithPlanRoute>
                    }
                  />
                  {/* Geral */}
                  <Route
                    path="geral"
                    element={
                      <ProtectedWithPlanRoute
                        allowedForMinistryLeader={["/ministerios/escalas"]}
                      >
                        <GeneralMonthlySchedulePage />
                      </ProtectedWithPlanRoute>
                    }
                  />

                  {/* Specific Schedule */}
                  <Route
                    path=":ministryId"
                    element={
                      <ProtectedWithPlanRoute
                        allowedForMinistryLeader={["/ministerios/escalas"]}
                      >
                        <MinistrySchedulePage />
                      </ProtectedWithPlanRoute>
                    }
                  />
                </Route>

                {/* Jornadas Sub-module */}
                <Route path="jornadas">
                  <Route
                    index
                    element={
                      <ProtectedWithPlanRoute
                        allowedForMinistryLeader={["/ministerios/jornadas"]}
                      >
                        <JornadasPage />
                      </ProtectedWithPlanRoute>
                    }
                  />
                  <Route
                    path="nova"
                    element={
                      <ProtectedWithPlanRoute
                        allowedForMinistryLeader={["/ministerios/jornadas"]}
                      >
                        <NovaJornadaPage />
                      </ProtectedWithPlanRoute>
                    }
                  />
                  <Route
                    path=":id"
                    element={
                      <ProtectedWithPlanRoute
                        allowedForMinistryLeader={["/ministerios/jornadas"]}
                      >
                        <JornadaDetalhesPage />
                      </ProtectedWithPlanRoute>
                    }
                  />
                </Route>
              </Route>

              {/* Projetos routes */}
              <Route
                path="/projetos"
                element={
                  <ProtectedWithPlanRoute>
                    <ProjetosLayout />
                  </ProtectedWithPlanRoute>
                }
              >
                <Route index element={<ProjetosDashboardPage />} />
                <Route path=":id" element={<ProjetoKanbanPage />} />
              </Route>

              {/* Treinamentos routes */}
              <Route
                path="/treinamentos"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/treinamentos"]}
                  >
                    <TreinamentosPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/treinamentos/:id"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/treinamentos"]}
                  >
                    <TreinamentoDetalhesPage />
                  </ProtectedWithPlanRoute>
                }
              />

              {/* Redirects para retrocompatibilidade ou acesso direto antigo */}
              <Route
                path="/ministerios-dash"
                element={<Navigate to="/ministerios" replace />}
              />
              <Route
                path="/escalas"
                element={<Navigate to="/ministerios/escalas" replace />}
              />
              <Route
                path="/escalas/geral"
                element={<Navigate to="/ministerios/escalas/geral" replace />}
              />

              {/* Grupos routes */}
              <Route
                path="/grupos"
                element={
                  <ProtectedWithPlanRoute>
                    <GruposPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/grupos/:id"
                element={
                  <ProtectedWithPlanRoute>
                    <GrupoDetalhesPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/grupos/configuracoes"
                element={
                  <ProtectedWithPlanRoute>
                    <GruposConfiguracoesPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/grupos/dashboard"
                element={
                  <ProtectedWithPlanRoute>
                    <GruposDashboardPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/grupos/organizacao"
                element={
                  <ProtectedWithPlanRoute>
                    <GruposOrganizacaoPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/grupos/mapa"
                element={
                  <ProtectedWithPlanRoute>
                    <GruposMapaPage />
                  </ProtectedWithPlanRoute>
                }
              />

              {/* Cantina routes */}
              <Route
                path="/cantina"
                element={
                  <ProtectedWithPlanRoute>
                    <CantinaLayout />
                  </ProtectedWithPlanRoute>
                }
              >
                <Route index element={<CantinaPage />} />
                <Route path="venda" element={<CantinaPOSPage />} />
                <Route path="cardapio" element={<CantinaMenuPage />} />
                <Route path="fiados" element={<CantinaTabsPage />} />
                <Route path="gastos" element={<CantinaGastosPage />} />
                <Route
                  path="faturamento"
                  element={<CantinaFaturamentoPage />}
                />
                <Route path="relatorios" element={<RelatoriosPage />} />
              </Route>

              {/* Loja routes */}
              <Route
                path="/loja"
                element={
                  <ProtectedWithPlanRoute>
                    <LojaLayout />
                  </ProtectedWithPlanRoute>
                }
              >
                <Route index element={<LojaPage />} />
                {/* Mesmas telas da Cantina: a loja vem do LojaLayout. */}
                <Route path="produtos" element={<CantinaMenuPage />} />
                <Route path="venda" element={<CantinaPOSPage />} />
              </Route>

              {/* Configurações routes */}
              <Route
                path="/configuracoes/igreja"
                element={
                  <ProtectedWithPlanRoute>
                    <ConfiguracoesIgreja />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/configuracoes/usuarios"
                element={
                  <ProtectedWithPlanRoute>
                    <ConfiguracoesUsuarios />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/configuracoes/assinatura"
                element={
                  <ProtectedRoute skipEmailVerification>
                    <ConfiguracoesAssinatura />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuracoes/afiliados"
                element={
                  <ProtectedRoute>
                    <ConfiguracoesAfiliados />
                  </ProtectedRoute>
                }
              />
              {/* A tela de notificações virou a aba Preferências do modal
                  "Minha conta". O redirect fica porque o rodapé de todo e-mail
                  transacional já enviado aponta para este caminho. */}
              <Route
                path="/configuracoes/notificacoes"
                element={
                  <Navigate to="/dashboard?conta=preferencias" replace />
                }
              />
              <Route
                path="/configuracoes/pagamentos"
                element={
                  <ProtectedWithPlanRoute>
                    <ConfiguracoesPagamentos />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/configuracoes/pagamentos/callback"
                element={
                  <ProtectedWithPlanRoute>
                    <ConfiguracoesPagamentos />
                  </ProtectedWithPlanRoute>
                }
              />

              {/* Relatórios routes */}
              <Route
                path="/relatorios"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={[
                      "/relatorios",
                      "/relatorios/presenca-escalas",
                      "/relatorios/aniversariantes",
                    ]}
                  >
                    <Relatorios />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/membros-por-ministerio"
                element={
                  <ProtectedWithPlanRoute>
                    <MembrosPorMinisterio />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/crescimento-membros"
                element={
                  <ProtectedWithPlanRoute>
                    <CrescimentoMembros />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/demografia-membros"
                element={
                  <ProtectedWithPlanRoute>
                    <DemografiaMembros />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/qualidade-cadastro"
                element={
                  <ProtectedWithPlanRoute>
                    <QualidadeCadastro />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/frequentadores"
                element={
                  <ProtectedWithPlanRoute>
                    <FrequentadoresReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/atividade-membros"
                element={
                  <ProtectedWithPlanRoute>
                    <AtividadeMembros />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/crescimento-ministerios"
                element={
                  <ProtectedWithPlanRoute>
                    <CrescimentoMinisterios />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/aniversariantes"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/relatorios/aniversariantes"]}
                  >
                    <Aniversariantes />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/visitantes"
                element={
                  <ProtectedWithPlanRoute>
                    <VisitantesReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/visitantes-convertidos"
                element={
                  <ProtectedWithPlanRoute>
                    <VisitantesConvertidosReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/infantil-retirada"
                element={
                  <ProtectedWithPlanRoute>
                    <KidsPickupReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/infantil-salas"
                element={
                  <ProtectedWithPlanRoute>
                    <KidsRoomsReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/infantil-visitantes"
                element={
                  <ProtectedWithPlanRoute>
                    <KidsVisitorsReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/infantil-fichas"
                element={
                  <ProtectedWithPlanRoute>
                    <KidsRecordsReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/apelos"
                element={
                  <ProtectedWithPlanRoute>
                    <ApelosReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/chamada"
                element={
                  <ProtectedWithPlanRoute>
                    <Chamadas />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/financeiros"
                element={
                  <ProtectedWithPlanRoute>
                    <FinanceReportsPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/fluxo-de-caixa"
                element={
                  <ProtectedWithPlanRoute>
                    <FluxoDeCaixaReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/contribuintes"
                element={
                  <ProtectedWithPlanRoute>
                    <ContribuintesReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/contas-a-pagar"
                element={
                  <ProtectedWithPlanRoute>
                    <ContasAPagarReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/grupos"
                element={
                  <ProtectedWithPlanRoute>
                    <GruposRelatoriosPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/batismos"
                element={
                  <ProtectedWithPlanRoute>
                    <BatismosReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/batizados-na-igreja"
                element={
                  <ProtectedWithPlanRoute>
                    <BatizadosNaIgrejaPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/esfriamento"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/relatorios/esfriamento"]}
                  >
                    <EsfriamentoReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/comparativo-crescimento"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={[
                      "/relatorios/comparativo-crescimento",
                    ]}
                  >
                    <ComparativoCrescimentoPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/carga-escala"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/relatorios/carga-escala"]}
                  >
                    <CargaEscalaReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/cobertura-posicoes"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/relatorios/cobertura-posicoes"]}
                  >
                    <CoberturaPosicoesReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/disponibilidade"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/relatorios/disponibilidade"]}
                  >
                    <DisponibilidadeReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/campos-personalizados"
                element={
                  <ProtectedWithPlanRoute>
                    <CamposPersonalizadosReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/cultos"
                element={
                  <ProtectedWithPlanRoute>
                    <ServiceReportsPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/presenca-escalas"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={[
                      "/relatorios",
                      "/relatorios/presenca-escalas",
                    ]}
                  >
                    <PresencaEscalasReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              <Route
                path="/relatorios/presenca-escalas/:memberId"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={[
                      "/relatorios",
                      "/relatorios/presenca-escalas",
                    ]}
                  >
                    <MemberScheduleReportPage />
                  </ProtectedWithPlanRoute>
                }
              />
              {/* Visitantes - interna (protegida) */}
              <Route
                path="/visitantes"
                element={
                  <ProtectedWithPlanRoute>
                    <Visitantes />
                  </ProtectedWithPlanRoute>
                }
              />
              {/* Linha do Tempo: virou o card Histórico da ficha */}
              <Route
                path="/pessoas/linha-do-tempo"
                element={<LinhaDoTempoLegacyRedirect />}
              />
              {/* Bio Links - interna (protegida) */}
              <Route
                path="/bio-links"
                element={
                  <ProtectedWithPlanRoute>
                    <BioLinksPage />
                  </ProtectedWithPlanRoute>
                }
              />
              {/* Apelo - interna (protegida) */}
              <Route
                path="/apelo"
                element={
                  <ProtectedWithPlanRoute>
                    <ApeloPage />
                  </ProtectedWithPlanRoute>
                }
              />
              {/* Chamada - interna (protegida) */}
              <Route
                path="/chamada"
                element={
                  <ProtectedWithPlanRoute
                    allowedForMinistryLeader={["/chamada"]}
                  >
                    <RollCallPage />
                  </ProtectedWithPlanRoute>
                }
              />

              {/* Cultos - Contagem */}

              {/* Cronograma - rotas protegidas */}

              {/* Agenda routes */}
              {/* Atividades Routes */}
              <Route
                path="/atividades"
                element={
                  <ProtectedWithPlanRoute>
                    <AtividadesLayout />
                  </ProtectedWithPlanRoute>
                }
              >
                <Route index element={<AtividadesPage />} />

                {/* Events */}
                <Route path="events" element={<EventsPage />} />
                <Route
                  path="events/inscricoes/:eventId"
                  element={<EventRegistrationsPage />}
                />
                <Route
                  path="events/formulario/:eventId"
                  element={<EventFormBuilderPage />}
                />
                <Route
                  path="events/configuracoes/:eventId"
                  element={<EventSettingsPage />}
                />
                <Route
                  path="events/analytics/:eventId"
                  element={<EventAnalyticsPage />}
                />
                <Route
                  path="events/frequencia/:eventId"
                  element={<EventAttendancePage />}
                />

                {/* Cronograma */}
                <Route path="cronograma" element={<CronogramaPage />} />
                <Route
                  path="cronograma/novo"
                  element={<NovoCronogramaPage />}
                />
                <Route
                  path="cronograma/editar/:id"
                  element={<EditarCronogramaPage />}
                />
                <Route
                  path="cronograma/cultos-recorrentes"
                  element={<CultosRecorrentesPage />}
                />

                {/* Agenda */}
                <Route path="agenda" element={<AgendaPage />} />

                {/* Corrente de Oração */}
                <Route path="oracao" element={<PrayerChainsPage />} />
                <Route
                  path="oracao/configuracoes/:chainId"
                  element={<PrayerChainSettingsPage />}
                />
                <Route
                  path="oracao/:chainId"
                  element={<PrayerChainPanelPage />}
                />

                {/* Contagem */}
                <Route path="contagem" element={<ServiceCountsPage />} />

                {/* Evangelismo */}
                <Route path="evangelismo" element={<EvangelismListPage />} />
                <Route
                  path="evangelismo/:campaignId"
                  element={<EvangelismCampaignPage />}
                />

                {/* Votações */}
                <Route path="votacoes" element={<PollsListPage />} />
                <Route path="votacoes/:pollId" element={<PollDetailsPage />} />
              </Route>

              {/* Kids routes */}
              <Route
                path="/kids"
                element={
                  <ProtectedWithPlanRoute>
                    <KidsLayout />
                  </ProtectedWithPlanRoute>
                }
              >
                <Route index element={<KidsPage />} />
                <Route path="faixas-etarias" element={<FaixasEtariasPage />} />
                <Route path="criancas" element={<CriancasPage />} />
                <Route
                  path="criancas/cadastro"
                  element={<CadastroCriancaPage />}
                />
                <Route
                  path="criancas/visualizar/:id"
                  element={<VisualizarCriancaPage />}
                />
                <Route
                  path="criancas/editar/:id"
                  element={<EditarCriancaPage />}
                />
                <Route path="check-in" element={<CheckInPage />} />
                <Route path="sala/:id" element={<SalaPage />} />
                <Route path="frequencia" element={<FrequenciaPage />} />
                <Route path="impressao" element={<KidsPrintStationPage />} />
                <Route
                  path="tela-checkin"
                  element={<KidsCheckInScreenPage />}
                />
              </Route>

              {/* Batismos routes */}
              <Route
                path="/batismos"
                element={
                  <ProtectedWithPlanRoute>
                    <BatismosDashboardPage />
                  </ProtectedWithPlanRoute>
                }
              />
              {/* Endereços antigos: candidatos, turmas e eventos viraram abas */}
              <Route path="/batismos/candidatos/*" element={<BaptismQueueRedirect />} />
              <Route path="/batismos/turmas/*" element={<BaptismHomeRedirect />} />
              <Route path="/batismos/eventos" element={<BaptismHomeRedirect />} />
              <Route path="/batismos/eventos/novo" element={<BaptismHomeRedirect />} />
              <Route path="/batismos/eventos/:id" element={<BaptismEventRedirect />} />
              {/* Logs de Auditoria - Admin only (menu visibility controlled by isAdmin) */}
              <Route
                path="/configuracoes/logs"
                element={
                  <ProtectedWithPlanRoute>
                    <LogsPage />
                  </ProtectedWithPlanRoute>
                }
              />

              {/* Obrigado - página protegida para tracking de conversões */}
              <Route
                path="/obrigado/:subscriptionId"
                element={
                  <ProtectedRoute>
                    <ObrigadoPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Bio Links - pública (Novo formato encurtado) — fora do AppProviders */}
            <Route element={<PublicPlanGuard resolver="bioSlug" />}>
              <Route path="/:slug" element={<BioPublicPage />} />
            </Route>

            {/* 404 Catch-all Route (Sempre por último) — fora do AppProviders */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
      </Suspense>
    </>
  );
}
